/**
 * requestIdleCallback shim with Safari fallback (Feb 2026).
 *
 * Schedules a low-priority task that runs only when the browser is
 * idle — perfect for cache writes / hash computations that must NOT
 * compete with keystroke rendering on the main thread.
 *
 * - On Chrome / Firefox: uses native ``requestIdleCallback`` with
 *   the supplied timeout (default 2s) so the work is guaranteed to
 *   run even on a perpetually busy main thread.
 * - On Safari (no native support): falls back to ``setTimeout(fn, 1)``
 *   which yields to the event loop once before running — much less
 *   ideal than true idle deferral, but still gets the work off the
 *   active animation frame.
 *
 * Returns a cancel function — callers MUST keep it around and call
 * it when a newer task supersedes the pending one or when the
 * component unmounts.  Forgetting to cancel produces zombie writes
 * after the user has moved on, which could clobber a freshly-saved
 * cache key with stale data.
 */
export function scheduleIdle(fn, { timeout = 2000 } = {}) {
  if (typeof window === 'undefined') {
    // SSR / Node test env — run sync.
    try { fn(); } catch (e) { console.error('[scheduleIdle] sync run failed:', e); }
    return () => {};
  }
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(fn, { timeout });
    return () => {
      try { window.cancelIdleCallback?.(id); } catch {}
    };
  }
  // Safari + older browsers: setTimeout fallback.
  const id = window.setTimeout(fn, 1);
  return () => window.clearTimeout(id);
}
